import { useCallback, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { App, Modal } from 'antd'
import type { MenuProps } from 'antd'
import { Link2, Mail } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { SurveyService } from '../services'
import { MindmapSurveyService } from '../services/mindmapSurveyService'
import { getAppBaseUrl } from '../../../utils/appUrl'
import { analytics } from '../../../utils/analytics'

export interface UseSurveyInsightsActionsOptions {
  nodeId: string | undefined
  mindmapId: string | undefined
  parentLabel: string | null | undefined
  /** Called after a destructive/state-changing action so the page can refetch. */
  onResponsesChanged: () => void
}

export interface UseSurveyInsightsActionsResult {
  handleSimulate: () => Promise<void>
  handleDelete: () => void
  shareMenuItems: MenuProps['items']
  simulating: boolean
}

/**
 * Encapsulates the secondary actions on the survey insights page (simulate
 * responses, copy/email share, delete) so the page component stays focused on
 * data loading and rendering.
 */
export function useSurveyInsightsActions({
  nodeId,
  mindmapId,
  parentLabel,
  onResponsesChanged,
}: UseSurveyInsightsActionsOptions): UseSurveyInsightsActionsResult {
  const { notification } = App.useApp()
  const navigate = useNavigate()
  const [simulating, setSimulating] = useState(false)

  const handleSimulate = useCallback(async () => {
    if (!nodeId) return
    setSimulating(true)
    const res = await SurveyService.simulate(nodeId)
    setSimulating(false)
    if (res.error || !res.data) {
      notification.error({ title: 'Could not generate simulated responses. Please try again.' })
      return
    }
    notification.success({ title: `${res.data.count} simulated responses added` })
    onResponsesChanged()
  }, [nodeId, notification, onResponsesChanged])

  const handleCopyLink = useCallback(async () => {
    if (!nodeId) return
    const url = `${getAppBaseUrl()}/s/${nodeId}`
    try {
      await navigator.clipboard.writeText(url)
      notification.success({ title: 'Link copied' })
      analytics.surveyShared({ node_id: nodeId, method: 'copy_link' })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'survey', action: 'copy_link' },
      })
      notification.error({ title: 'Could not copy link' })
    }
  }, [nodeId, notification])

  const handleSendByEmail = useCallback(() => {
    if (!nodeId) return
    const url = `${getAppBaseUrl()}/s/${nodeId}`
    const subject = encodeURIComponent(
      parentLabel ? `Quick survey: ${parentLabel}` : 'Quick survey — your opinion matters'
    )
    const body = encodeURIComponent(
      `Hi,\n\nI'm gathering feedback${parentLabel ? ` on "${parentLabel}"` : ''} and would love your perspective.\n\nTakes just a few minutes:\n\n${url}\n\nThank you!`
    )
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
    analytics.surveyShared({ node_id: nodeId, method: 'email' })
  }, [nodeId, parentLabel])

  const shareMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'copy-link',
        icon: <Link2 size={13} />,
        label: 'Copy link',
        onClick: () => void handleCopyLink(),
      },
      {
        key: 'send-email',
        icon: <Mail size={13} />,
        label: 'Send by email',
        onClick: () => handleSendByEmail(),
      },
    ],
    [handleCopyLink, handleSendByEmail]
  )

  const handleDelete = useCallback(() => {
    if (!mindmapId || !nodeId) return
    const label = parentLabel && parentLabel.length > 0 ? parentLabel : 'Untitled survey'
    Modal.confirm({
      title: 'Delete this survey?',
      content: `"${label}" and all of its responses will be permanently deleted. This can't be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        const res = await MindmapSurveyService.delete(mindmapId, nodeId)
        if (res.error) {
          notification.error({ title: 'Failed to delete survey' })
          return
        }
        notification.success({ title: 'Survey deleted' })
        void navigate(`/mindmap/${mindmapId}/research`, { replace: true })
      },
    })
  }, [mindmapId, nodeId, parentLabel, navigate, notification])

  return { handleSimulate, handleDelete, shareMenuItems, simulating }
}
